import Checkbox from "../Checkbox";
import LinedInput, {InputType} from "../LinedInput";
import React, {useRef} from "react";
import styles from "./BankAccountInfo.module.css";
import useFocusInvalid from "../../lib/useFocusInvalid";
import {SecuredDepositKeys} from "../../lib/api/models";

const strings = {
    accountNumberLabel: "Account Number",
    checkboxLabel: "Save this account info for future use",
    checkboxWarrant: "I warrant I am the owner or an authorized user of this account",
    confirmAccountNumberLabel: "Confirm Account Number",
    nameOnAccountLabel: "Name on Account",
    abaNumberLabel: "Routing Number",
};

export const MAX_ROUTING_NUMBER_LENGTH = 9;
export const MIN_ACCOUNT_NUMBER_LENGTH = 4;
export const MAX_ACCOUNT_NUMBER_LENGTH = 14;

type Props = {
    name: string;
    onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onChange: (name: SecuredDepositKeys, value: string) => void;
    abaNumber: string;
    accountNumber: string;
    confirmAccountNumber: string;
    setConfirmAccountNumber: (n: string) => void;
    isChecked: boolean;
    setIsChecked: (b: boolean) => void;
    isWarrantChecked: boolean;
    setIsWarrantChecked: (b: boolean) => void;
    firstInvalidKey?: string;
    validationCount: number;
    abaNumberToggleText?: string;
    accountNumberToggleText?: string;
    validationAbaNumber?: string[];
    validationAccountNumber?: string[];
    validationConfirmAccountNumber?: string[];
    className?: string;
};

export default function BankAccountInfo(props: Props) {
    const abaNumberRef = useRef<HTMLInputElement>(null);
    const accountNumberRef = useRef<HTMLInputElement>(null);
    const confirmAccountNumberRef = useRef<HTMLInputElement>(null);

    let invalidRef;
    if (props.firstInvalidKey === SecuredDepositKeys.AbaNumber) {
        invalidRef = abaNumberRef;
    } else if (props.firstInvalidKey === SecuredDepositKeys.AccountNumber) {
        invalidRef = accountNumberRef;
    } else if (props.firstInvalidKey === SecuredDepositKeys.ConfirmAccountNumber) {
        invalidRef = confirmAccountNumberRef;
    }
    useFocusInvalid(props.validationCount, invalidRef?.current);

    return (
        <div className={props.className}>
            <LinedInput
                id="accountName"
                label={strings.nameOnAccountLabel}
                value={props.name}
                type={InputType.Text}
                readOnly={true}
            />
            <LinedInput
                id={SecuredDepositKeys.AbaNumber}
                name={SecuredDepositKeys.AbaNumber}
                ref={abaNumberRef}
                required={true}
                value={props.abaNumber}
                setValue={(value) => props.onChange(SecuredDepositKeys.AbaNumber, value)}
                onBlur={props.onBlur}
                label={strings.abaNumberLabel}
                type={InputType.Text}
                toggleText={props.abaNumberToggleText}
                minLength={MAX_ROUTING_NUMBER_LENGTH}
                maxLength={MAX_ROUTING_NUMBER_LENGTH}
                validationMessage={props.validationAbaNumber}
            />
            <LinedInput
                id={SecuredDepositKeys.AccountNumber}
                name={SecuredDepositKeys.AccountNumber}
                ref={accountNumberRef}
                required={true}
                value={props.accountNumber}
                setValue={(value) => props.onChange(SecuredDepositKeys.AccountNumber, value)}
                onBlur={props.onBlur}
                label={strings.accountNumberLabel}
                minLength={MIN_ACCOUNT_NUMBER_LENGTH}
                maxLength={MAX_ACCOUNT_NUMBER_LENGTH}
                type={InputType.Text}
                toggleText={props.accountNumberToggleText}
                validationMessage={props.validationAccountNumber}
            />
            <LinedInput
                id={SecuredDepositKeys.ConfirmAccountNumber}
                name={SecuredDepositKeys.ConfirmAccountNumber}
                ref={confirmAccountNumberRef}
                required={true}
                value={props.confirmAccountNumber}
                setValue={props.setConfirmAccountNumber}
                onBlur={props.onBlur}
                label={strings.confirmAccountNumberLabel}
                minLength={MIN_ACCOUNT_NUMBER_LENGTH}
                maxLength={MAX_ACCOUNT_NUMBER_LENGTH}
                type={InputType.Text}
                validationMessage={props.validationConfirmAccountNumber}
            />
            <Checkbox
                className={styles.checkbox}
                isChecked={props.isChecked}
                onChange={(e) => props.setIsChecked(e.target.checked)}
                label={strings.checkboxLabel}
            />
            <Checkbox
                className={styles.checkbox}
                isChecked={props.isWarrantChecked}
                onChange={(e) => props.setIsWarrantChecked(e.target.checked)}
                label={strings.checkboxWarrant}
                required={true}
            />
        </div>
    );
}
